import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Grid,
  Card,
  Typography,
  CircularProgress,
  Container,
  Divider,
  Chip,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";

const SurveyResponse = () => {
  const { survey_token } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [serviceList, setServiceList] = useState([]);
  const [surveyDetails, setSurveyDetails] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  console.log('serviceList', serviceList);

  useEffect(() => {
    getServicesList(survey_token);
  }, [survey_token]);

  const getServicesList = async (survey_token) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        "https://api.nopapertimesheet.com/api/lead/get-details",
        {
          params: { uuid: survey_token },
        }
      );

      if (response && response?.data?.s === 0) {
        navigate("/");
        return;
      }

      if (response && response?.data?.s) {
        const serviceListQuestionAnswer = JSON.parse(response?.data?.r?.question_answer || '[]');
        setServiceList(serviceListQuestionAnswer);
        setSurveyDetails(response?.data?.r);
      } else {
        setServiceList([]);
      }
    } catch (error) {
      console.error("Error fetching survey:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #0d1229 0%, #1a237e 100%)",
        }}
      >
        <CircularProgress color="secondary" size={60} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Survey Responses | NoPaperTimesheet</title>
        <meta name="description" content="View your submitted survey responses" />
      </Helmet>

      <Box sx={{
        minHeight: '100vh',
        backgroundColor: 'rgba(13, 18, 41, 1)',
        py: 6,
        px: isMobile ? 2 : 0
      }}>
        <Container maxWidth="md">
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Box sx={{
              background: 'linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)',
              p: 3,
              textAlign: 'center'
            }}>
              <Typography variant="h4" component="h1" sx={{
                color: '#fff',
                fontWeight: 700,
                mb: 1
              }}>
                Survey Submission Details
              </Typography>
              {surveyDetails && (
                <>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Submission Date: {new Date(surveyDetails?.created_at).toLocaleDateString()}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Referred Source: {surveyDetails?.caregiver_details?.f_name + " " + surveyDetails?.caregiver_details?.l_name}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Completed the Survey: {surveyDetails?.name}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Lead Phone Number: {`+${surveyDetails?.phno_cc} ${surveyDetails?.phno}`}
                  </Typography>
                </>

              )}
            </Box>

            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              {serviceList?.questions?.length > 0 ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4
                }}>
                  {serviceList.questions.map((question, index) => (
                    <Box key={index} sx={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      p: 3,
                      borderLeft: '4px solid #4caf50',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Chip
                          label={`Q${index + 1}`}
                          color="primary"
                          size="small"
                          sx={{
                            mr: 2,
                            fontWeight: 600,
                            fontSize: '0.8rem'
                          }}
                        />
                        <Typography variant="h6" sx={{
                          color: '#fff',
                          fontWeight: 500,
                          flexGrow: 1
                        }}>
                          {question?.label}
                        </Typography>
                      </Box>

                      <Box sx={{
                        background: 'rgba(107, 207, 207, 0.2)',
                        borderRadius: 1,
                        p: 2,
                        borderLeft: '3px solid #6bcfcf'
                      }}>
                        {Array.isArray(question?.answer) ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {question?.answer?.map((item, i) => (
                              <Chip
                                key={i}
                                label={item}
                                color="secondary"
                                size="small"
                                sx={{ mb: 1 }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1" sx={{
                            color: '#e0f7fa',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {question?.answer || <em style={{ color: '#aaa' }}>No answer provided</em>}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  py: 6,
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 2
                }}>
                  <Typography variant="h6" sx={{
                    color: '#fff',
                    mb: 2
                  }}>
                    No Responses Found
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}>
                    There are no responses available for this survey.
                  </Typography>
                </Box>
              )}
            </CardContent>

            <Box sx={{
              p: 3,
              textAlign: 'center',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Typography variant="body2" sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontStyle: 'italic'
              }}>
                Thank you for completing our survey!
              </Typography>
            </Box>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default SurveyResponse;